/**
 * aiBusy.ts — Dogfood-fix-wave-1 F8
 *
 * Global counter tracking in-flight AI / LLM-backed mutations.
 * When count > 0, the browser cursor is set to 'wait'.
 *
 * Usage:
 *   import { aiBusy } from '@/lib/aiBusy';
 *   aiBusy.incr();
 *   try { await runMutation(); } finally { aiBusy.decr(); }
 *
 * The AiBusyOverlay component subscribes and applies the cursor.
 * Use a single shared module instance (singleton) — Zustand create() produces
 * a stable store across all hot-reload cycles in Vite dev mode.
 */

import { create } from 'zustand';

interface AiBusyState {
  count: number;
  incr: () => void;
  decr: () => void;
  isBusy: () => boolean;
}

export const useAiBusyStore = create<AiBusyState>((set, get) => ({
  count: 0,
  incr: () => set((s) => ({ count: s.count + 1 })),
  decr: () => set((s) => ({ count: Math.max(0, s.count - 1) })),
  isBusy: () => get().count > 0,
}));

/**
 * Imperative helper for use inside mutation callbacks (outside React).
 * Wraps the Zustand store's incr/decr methods.
 */
export const aiBusy = {
  incr: () => useAiBusyStore.getState().incr(),
  decr: () => useAiBusyStore.getState().decr(),
};
